/**
 * StockInfoCard - Smart stock information widget
 *
 * Displays:
 * - Ticker symbol and company name
 * - Current price with change indicator
 * - Mini price chart (1 year history - auto-fetched)
 * - Key metrics (P/E, P/B, Market Cap, etc.)
 * - Tabs for multiple tickers
 *
 * Auto-fetches 1-year price history for sparkline display
 */

import { useMemo, useState, useEffect, useRef } from 'react';
import Highcharts from 'highcharts';
import 'highcharts/modules/stock';
import { getChartTheme, getPriceColor, WIDGET_BACKGROUND } from './chartTheme';
import { callWidgetApi } from '../../utils/widgetApi';
import { useContainerWidth } from './hooks/useContainerWidth';

/**
 * Normalize stock/index/commodity data to consistent field names
 * Different asset types use different field names:
 * - Index: indexClose, indexHigh, indexLow, etc.
 * - Commodity/Gold/Crypto: name, indexClosePrev, etc.
 * This function normalizes them to stock field names (priceClose, priceHigh, etc.)
 *
 * @param {object} data - Raw stock, index, or commodity data
 * @param {string} assetType - Asset type: 'stock', 'index', 'gold', 'commodity', 'crypto', etc.
 * @returns {object} - Normalized data with consistent field names
 */
function normalizeStockData(data, assetType = 'stock') {
    if (!data) return data;

    // Stock type - already has correct field names
    if (assetType === 'stock') return data;

    // Non-stock types need field mapping
    // Common mapping for index, gold, commodity, crypto, economy
    return {
        ...data,
        // Price fields mapping
        priceClose: data.priceClose ?? data.indexClose ?? data.indexCurrent,
        priceOpen: data.priceOpen ?? data.indexOpenIntraDay ?? data.indexOpen,
        priceHigh: data.priceHigh ?? data.indexHigh ?? data.close52wHigh,
        priceLow: data.priceLow ?? data.indexLow ?? data.close52wLow,
        priceRef: data.priceRef ?? data.indexClosePrev ?? data.indexReference,
        // Name mapping - use 'name' field for non-stock types
        nameVi: data.nameVi ?? data.name ?? data.ticker,
        nameEn: data.nameEn ?? data.name ?? data.ticker,
        // Ensure ticker is set
        ticker: data.ticker ?? data.symbol,
    };
}

/**
 * Format number with Vietnamese locale
 */
function formatNumber(value, options = {}) {
    if (value === null || value === undefined) return '-';

    const {
        decimals = 2,
        suffix = '',
        prefix = '',
        compact = false
    } = options;

    if (compact && Math.abs(value) >= 1e9) {
        return `${prefix}${(value / 1e9).toFixed(1)}B${suffix}`;
    }
    if (compact && Math.abs(value) >= 1e6) {
        return `${prefix}${(value / 1e6).toFixed(1)}M${suffix}`;
    }
    if (compact && Math.abs(value) >= 1e3) {
        return `${prefix}${(value / 1e3).toFixed(1)}K${suffix}`;
    }

    return `${prefix}${value.toLocaleString('vi-VN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    })}${suffix}`;
}

/**
 * Get URL for ticker based on asset type
 * - stock: https://simplize.vn/co-phieu/{ticker}
 * - other (index, commodity, crypto, etc.): https://simplize.vn/chart?ticker={ticker}
 */
function getTickerUrl(ticker, type = 'stock', includeAnalysis = false) {
    if (type === 'stock') {
        return includeAnalysis
            ? `https://simplize.vn/co-phieu/${ticker}/phan-tich`
            : `https://simplize.vn/co-phieu/${ticker}`;
    }
    // For index, commodity, crypto, economy, etc. - link to chart page
    return `https://simplize.vn/chart?ticker=${ticker}`;
}

/**
 * Format price change with color
 * @param {number} value - Net change value
 * @param {number} percent - Percent change
 * @param {number} decimals - Number of decimal places for value (default: 0, index uses 2)
 */
function PriceChange({ value, percent, decimals = 0 }) {
    if (value === null || value === undefined) return null;

    const isPositive = value > 0;
    const isNegative = value < 0;

    const colorClass = isPositive
        ? 'text-green-600 dark:text-green-400'
        : isNegative
            ? 'text-red-600 dark:text-red-400'
            : 'text-gray-600 dark:text-gray-400';

    const sign = isPositive ? '+' : '';

    return (
        <span className={colorClass}>
            {sign}{formatNumber(value, { decimals })}
            {percent !== undefined && (
                <span className="ml-1">({sign}{formatNumber(percent, { decimals: 2 })}%)</span>
            )}
        </span>
    );
}

/**
 * Metric row component
 */
function MetricRow({ label, value, suffix = '' }) {
    return (
        <div className="flex justify-between items-center py-1 border-b border-gray-100 dark:border-gray-700 last:border-0">
            <span className="text-gray-500 dark:text-gray-400 text-sm">{label}</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">
                {value !== null && value !== undefined ? `${value}${suffix}` : '-'}
            </span>
        </div>
    );
}

/**
 * Stock colors for comparison chart
 */
const STOCK_COLORS = [
    '#ef4444', // red
    '#3b82f6', // blue
    '#22c55e', // green
    '#f59e0b', // amber
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#84cc16', // lime
];

/**
 * Comparison chart - merged view for multiple stocks (% change)
 * @param colorMap - Optional map of ticker -> color for consistent colors when filtering
 * @param chartHeight - Responsive chart height
 */
function ComparisonChart({ allPriceData, tickers, isDarkMode, timeRange = '1Y', colorMap = {}, chartHeight = 220 }) {
    const chartRef = useRef(null);

    useEffect(() => {
        if (!chartRef.current || !allPriceData || Object.keys(allPriceData).length === 0) return;

        const now = Date.now();
        const getFromMs = (range) => {
            if (range === 'YTD') return Date.UTC(new Date().getUTCFullYear(), 0, 1);
            const days = { '1D': 1, '1M': 30, '6M': 180, '1Y': 365, '5Y': 1825 }[range];
            return days ? now - days * 86400000 : 0;
        };
        const fromMs = getFromMs(timeRange);

        // Build series for each ticker (normalized to % change)
        const series = tickers.map((ticker, idx) => {
            const raw = allPriceData[ticker] || [];
            const priceData = raw.filter(item => {
                const ts = Array.isArray(item) ? item[0] : item.timestamp;
                const tsMs = ts < 1e12 ? ts * 1000 : ts;
                return tsMs >= fromMs;
            });
            if (priceData.length === 0) return null;

            // Normalize to % change from first price of filtered period
            const firstPrice = priceData[0]?.[4] || priceData[0]?.close || 0;
            const chartData = priceData.map(item => {
                const [timestamp, , , , close] = Array.isArray(item) ? item : [item.timestamp, 0, 0, 0, item.close];
                const ts = timestamp < 1e12 ? timestamp * 1000 : timestamp;
                const pctChange = firstPrice > 0 ? ((close - firstPrice) / firstPrice) * 100 : 0;
                return [ts, pctChange];
            });

            // Use colorMap if provided, otherwise fall back to index
            const color = colorMap[ticker] || STOCK_COLORS[idx % STOCK_COLORS.length];

            return {
                name: ticker,
                data: chartData,
                color: color,
                lineWidth: 2
            };
        }).filter(Boolean);

        if (series.length === 0) return;

        // Use shared theme
        const theme = getChartTheme(isDarkMode);

        Highcharts.chart(chartRef.current, {
            chart: {
                ...theme.chart,
                type: 'line',
                height: chartHeight,
            },
            title: { text: null },
            credits: theme.credits,
            exporting: { enabled: false },
            legend: { enabled: false },
            xAxis: {
                ...theme.xAxis,
                type: 'datetime',
            },
            yAxis: {
                ...theme.yAxis,
                labels: {
                    ...theme.yAxis.labels,
                    format: '{value}%'
                },
                plotLines: [{
                    value: 0,
                    color: isDarkMode ? '#6b7280' : '#9ca3af',
                    dashStyle: 'dash',
                    width: 1
                }]
            },
            tooltip: {
                ...theme.tooltip,
                xDateFormat: '%d/%m/%Y',
                pointFormat: '<span style="color:{series.color}">{series.name}</span>: <b>{point.y:.2f}%</b><br/>'
            },
            plotOptions: {
                line: {
                    marker: { enabled: false },
                    states: {
                        hover: { lineWidth: 3 }
                    }
                }
            },
            series: series
        });
    }, [allPriceData, tickers, isDarkMode, timeRange, chartHeight]);

    return (
        <div ref={chartRef} className="w-full" style={{ height: `${chartHeight}px` }} />
    );
}

/**
 * Stock row in comparison table
 * @param {number} decimals - Number of decimal places for price (default: 0, index uses 2)
 * @param {boolean} isMobile - Whether to show compact mobile layout
 */
function StockRow({ stockData, color, onClick, decimals = 0, isMobile = false }) {
    const { ticker, nameVi, nameEn, priceClose, netChange, pctChange, imageUrl, stockExchange } = stockData || {};
    const displayName = nameVi || nameEn || '';
    const isUp = (pctChange || 0) > 0;
    const isDown = (pctChange || 0) < 0;

    return (
        <div
            className={`flex items-center gap-2 px-3 border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer ${isMobile ? 'py-1.5' : 'py-2'}`}
            onClick={() => onClick?.(ticker)}
        >
            {/* Color indicator */}
            <div className={`w-1 rounded ${isMobile ? 'h-6' : 'h-8'}`} style={{ backgroundColor: color }} />

            {/* Logo - smaller on mobile */}
            {imageUrl ? (
                <img src={imageUrl} alt={ticker} className={`rounded-full object-contain bg-white ${isMobile ? 'w-6 h-6' : 'w-8 h-8'}`} />
            ) : (
                <div className={`rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs font-bold ${isMobile ? 'w-6 h-6' : 'w-8 h-8'}`}>
                    {ticker?.charAt(0)}
                </div>
            )}

            {/* Name & Ticker - on mobile only show ticker */}
            <div className="flex-1 min-w-0">
                {isMobile ? (
                    <div className="font-medium text-gray-900 dark:text-white text-xs">{ticker}</div>
                ) : (
                    <>
                        <div className="font-medium text-gray-900 dark:text-white text-sm">{displayName || ticker}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {ticker}{stockExchange ? ` · ${stockExchange}` : ''}
                        </div>
                    </>
                )}
            </div>

            {/* Price */}
            <div className={`text-right font-medium text-gray-900 dark:text-white ${isMobile ? 'text-xs min-w-[50px]' : 'text-sm min-w-[70px]'}`}>
                {formatNumber(priceClose, { decimals })}
            </div>

            {/* Net Change */}
            <div className={`text-right ${isMobile ? 'text-xs min-w-[45px]' : 'text-sm min-w-[60px]'} ${isUp ? 'text-green-600' : isDown ? 'text-red-600' : 'text-gray-500'}`}>
                {isUp ? '+' : ''}{formatNumber(netChange, { decimals })}
            </div>

            {/* Percent Change with pill */}
            <div className={`text-right font-medium rounded ${isMobile ? 'text-[10px] px-1.5 py-0.5 min-w-[50px]' : 'text-xs px-2 py-1 min-w-[60px]'} ${
                isUp
                    ? 'text-green-700 bg-green-50 dark:text-green-400 dark:bg-green-900/30'
                    : isDown
                        ? 'text-red-700 bg-red-50 dark:text-red-400 dark:bg-red-900/30'
                        : 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700'
            }`}>
                {isDown ? '▼' : isUp ? '▲' : ''} {Math.abs(pctChange || 0).toFixed(2)}%
            </div>
        </div>
    );
}

/**
 * Time range config for candlestick chart
 */
const CANDLESTICK_TIME_RANGES = {
    '1D': { days: 1, interval: '5m' },
    '1M': { days: 30, interval: '1h' },
    '6M': { days: 180, interval: '1d' },
    'YTD': { days: null, interval: '1d' },
    '1Y': { days: 365, interval: '1d' },
    '5Y': { days: 1825, interval: '1d' },
};

/**
 * Candlestick chart for single ticker in comparison view
 * Fetches data based on timeRange
 * @param chartHeight - Responsive chart height
 */
function ComparisonCandlestickChart({ ticker, timeRange, isDarkMode, chartHeight = 260 }) {
    const chartRef = useRef(null);
    const [priceData, setPriceData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Fetch data when ticker or timeRange changes
    useEffect(() => {
        if (!ticker) return;

        let cancelled = false;

        async function fetchData() {
            setLoading(true);
            try {
                const config = CANDLESTICK_TIME_RANGES[timeRange] || CANDLESTICK_TIME_RANGES['1Y'];
                const now = Math.floor(Date.now() / 1000);

                let from;
                if (timeRange === 'YTD') {
                    from = Math.floor(Date.UTC(new Date().getUTCFullYear(), 0, 1) / 1000);
                } else {
                    from = now - (config.days * 24 * 60 * 60);
                }

                const result = await callWidgetApi('get_historical_prices_range', {
                    tickers: [ticker],
                    interval: config.interval,
                    from_timestamp: from,
                    to_timestamp: now,
                    type: 'stock'
                });

                if (!cancelled) {
                    // Backend always returns {ticker: data} format
                    setPriceData(result.data[ticker] || []);
                }
            } catch (err) {
                console.warn('Failed to fetch candlestick data:', err);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        fetchData();

        return () => {
            cancelled = true;
        };
    }, [ticker, timeRange]);

    // Render chart when data changes
    useEffect(() => {
        if (loading) return;
        if (!chartRef.current || !priceData || priceData.length === 0) return;

        // Extract OHLC and volume data
        const ohlcData = [];
        const volumeData = [];

        priceData.forEach(item => {
            let ts, open, high, low, close, volume;
            if (Array.isArray(item)) {
                [ts, open, high, low, close, volume] = item;
            } else {
                ts = item.timestamp;
                open = item.open;
                high = item.high;
                low = item.low;
                close = item.close;
                volume = item.volume || item.totalVolume || 0;
            }
            const timestamp = ts < 1e12 ? ts * 1000 : ts;
            ohlcData.push([timestamp, open, high, low, close]);

            // Volume color based on price change
            const isUp = close >= open;
            volumeData.push({
                x: timestamp,
                y: volume || 0,
                color: getPriceColor(isUp, 0.5)
            });
        });

        const allPrices = ohlcData.flatMap(([, o, h, l, c]) => [o, h, l, c]).filter(Boolean);
        const dataMin = Math.min(...allPrices);
        const dataMax = Math.max(...allPrices);
        const padding = (dataMax - dataMin) * 0.02;

        // Use shared theme
        const theme = getChartTheme(isDarkMode);

        Highcharts.chart(chartRef.current, {
            chart: {
                ...theme.chart,
                height: chartHeight,
                zooming: { type: 'x' },
                panning: { enabled: true, type: 'x' },
                panKey: 'shift',
            },
            title: { text: null },
            credits: theme.credits,
            exporting: { enabled: false },
            legend: { enabled: false },
            xAxis: {
                ...theme.xAxis,
                type: 'datetime',
                ordinal: true,
                events: {
                    afterSetExtremes: function(e) {
                        const chart = this.chart;
                        const visible = ohlcData.filter(([t]) => t >= e.min && t <= e.max);
                        if (!visible.length) return;
                        const prices = visible.flatMap(([, o, h, l, c]) => [o, h, l, c]).filter(Boolean);
                        const vMin = Math.min(...prices);
                        const vMax = Math.max(...prices);
                        const pad = (vMax - vMin) * 0.02;
                        chart.yAxis[0].setExtremes(vMin - pad, vMax + pad, true, false);
                    }
                }
            },
            yAxis: [{
                // Price axis
                ...theme.yAxis,
                height: '75%',
                min: dataMin - padding,
                max: dataMax + padding,
                startOnTick: false,
                endOnTick: false,
                labels: {
                    ...theme.yAxis.labels,
                    align: 'right',
                    x: -5,
                    formatter: function() { return formatNumber(this.value, { decimals: 0 }); }
                },
                opposite: false
            }, {
                // Volume axis
                title: { text: null },
                top: '78%',
                height: '22%',
                offset: 0,
                gridLineWidth: 0,
                labels: { enabled: false }
            }],
            tooltip: {
                ...theme.tooltip,
                shared: true,
                formatter: function() {
                    const points = this.points;
                    const textColor = isDarkMode ? 'rgba(229, 229, 229, 0.9)' : 'rgba(51, 51, 51, 0.9)';
                    let html = `<div style="font-size:11px;margin-bottom:5px;color:${textColor}">${Highcharts.dateFormat('%d/%m/%Y', this.x)}</div>`;
                    points.forEach(point => {
                        if (point.series.type === 'candlestick') {
                            html += `<div style="padding:2px 0;color:${textColor}"><b>O:</b> ${formatNumber(point.point.open, { decimals: 0 })} <b>H:</b> ${formatNumber(point.point.high, { decimals: 0 })} <b>L:</b> ${formatNumber(point.point.low, { decimals: 0 })} <b>C:</b> ${formatNumber(point.point.close, { decimals: 0 })}</div>`;
                        } else if (point.series.type === 'column') {
                            html += `<div style="padding:2px 0;color:${textColor}"><b>KL:</b> ${formatNumber(point.y, { compact: true })}</div>`;
                        }
                    });
                    return html;
                }
            },
            plotOptions: {
                ...theme.plotOptions,
            },
            series: [{
                type: 'candlestick',
                name: ticker,
                data: ohlcData,
                yAxis: 0
            }, {
                type: 'column',
                name: 'Volume',
                data: volumeData,
                yAxis: 1
            }]
        });
    }, [priceData, ticker, isDarkMode, chartHeight, loading]);

    return <div ref={chartRef} className="w-full" style={{ height: `${chartHeight}px` }} />;
}

/**
 * Comparison view for multiple stocks
 * @param chartHeight - Responsive chart height
 * @param isMobile - Whether container is mobile size
 */
function StockComparisonView({ data, tickers, priceHistoryCache, loadingHistory, isDarkMode, tickerConfigMap = {}, chartHeight = 220, isMobile = false, timeRange, onTimeRangeChange }) {
    const [chartType, setChartType] = useState('line'); // 'line' or 'candlestick'
    // Default: all tickers selected for line chart
    const [selectedTickers, setSelectedTickers] = useState(new Set(tickers));
    // Active ticker for candlestick mode (first ticker by default)
    const [activeTicker, setActiveTicker] = useState(tickers[0]);

    // Update selected tickers when tickers prop changes
    useEffect(() => {
        setSelectedTickers(new Set(tickers));
        if (!tickers.includes(activeTicker)) {
            setActiveTicker(tickers[0]);
        }
    }, [tickers]);

    const timeRanges = [
        { label: '1D', value: '1D' },
        { label: '1M', value: '1M' },
        { label: '6M', value: '6M' },
        { label: 'YTD', value: 'YTD' },
        { label: '1Y', value: '1Y' },
        { label: '5Y', value: '5Y' },
    ];

    // Handle ticker click - different behavior based on chart type
    const handleTickerClick = (ticker) => {
        if (chartType === 'candlestick') {
            // In candlestick mode, clicking switches the active ticker
            setActiveTicker(ticker);
        } else {
            // In line mode, toggle selection
            setSelectedTickers(prev => {
                const newSet = new Set(prev);
                if (newSet.has(ticker)) {
                    if (newSet.size > 1) {
                        newSet.delete(ticker);
                    }
                } else {
                    newSet.add(ticker);
                }
                return newSet;
            });
        }
    };

    // Select all tickers (only for line mode)
    const selectAll = () => {
        setSelectedTickers(new Set(tickers));
    };

    // Get array of selected tickers (maintaining original order)
    const selectedTickersArray = tickers.filter(t => selectedTickers.has(t));

    // Create color map based on original ticker order (for consistent colors)
    const colorMap = useMemo(() => {
        const map = {};
        tickers.forEach((ticker, idx) => {
            map[ticker] = STOCK_COLORS[idx % STOCK_COLORS.length];
        });
        return map;
    }, [tickers]);

    // Compact mode: only show logo + ticker (mobile or >4 tickers on desktop)
    const showCompact = isMobile || tickers.length > 4;

    return (
        <>
            {/* Ticker chips - horizontal scroll, clickable to toggle/switch */}
            <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 overflow-x-auto scrollbar-hide">
                <div className="flex items-center gap-2 min-w-max">
                    {tickers.map((ticker, idx) => {
                        const tickerType = tickerConfigMap[ticker]?.type || 'stock';
                        const rawData = data[ticker] || data;
                        const tickerData = normalizeStockData(rawData, tickerType);
                        const priceClose = tickerData?.priceClose || 0;
                        const pctChange = tickerData?.pctChange || 0;
                        const imageUrl = tickerData?.imageUrl;
                        const priceDecimals = tickerType === 'index' ? 2 : 0;
                        const isUp = pctChange > 0;
                        const isDown = pctChange < 0;

                        // Different selection logic based on chart type
                        const isSelected = chartType === 'candlestick'
                            ? ticker === activeTicker
                            : selectedTickers.has(ticker);

                        return (
                            <button
                                key={ticker}
                                onClick={() => handleTickerClick(ticker)}
                                className={`flex items-center gap-1.5 pl-1.5 pr-2.5 py-1.5 rounded-full border transition-all whitespace-nowrap ${
                                    isSelected
                                        ? 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500'
                                        : 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-50'
                                }`}
                            >
                                {imageUrl ? (
                                    <img
                                        src={imageUrl}
                                        alt={ticker}
                                        className={`w-5 h-5 rounded-full object-contain flex-shrink-0 ${!isSelected ? 'grayscale' : ''}`}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                ) : null}
                                <div
                                    className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${imageUrl ? 'hidden' : ''}`}
                                    style={{ backgroundColor: isSelected ? STOCK_COLORS[idx % STOCK_COLORS.length] : '#9ca3af' }}
                                >
                                    {ticker?.charAt(0)}
                                </div>
                                <span className={`font-medium text-xs ${isSelected ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                                    {ticker}
                                </span>
                                {/* Price & Change - only show if not compact */}
                                {!showCompact && (
                                    <>
                                        <span className={`text-xs ${isSelected ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>
                                            {formatNumber(priceClose, { decimals: priceDecimals })}
                                        </span>
                                        <span className={`text-[10px] ${
                                            !isSelected ? 'text-gray-400' :
                                            isUp ? 'text-green-600' : isDown ? 'text-red-600' : 'text-gray-500'
                                        }`}>
                                            {isUp ? '+' : ''}{pctChange?.toFixed(2)}%
                                        </span>
                                    </>
                                )}
                            </button>
                        );
                    })}

                    {/* Select All button - only show in line mode when not all selected */}
                    {chartType === 'line' && selectedTickers.size < tickers.length && (
                        <button
                            onClick={selectAll}
                            className="px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 whitespace-nowrap"
                        >
                            Chọn tất cả
                        </button>
                    )}
                </div>
            </div>

            {/* Time range selector + Chart type toggle */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                {/* Time ranges */}
                <div className="flex gap-1">
                    {timeRanges.map(({ label, value }) => (
                        <button
                            key={value}
                            onClick={() => onTimeRangeChange(value)}
                            className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                                timeRange === value
                                    ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* Chart type toggle */}
                <div className="flex items-center gap-1 border border-gray-200 dark:border-gray-600 rounded p-0.5">
                    <button
                        onClick={() => setChartType('line')}
                        className={`p-1.5 rounded transition-colors ${
                            chartType === 'line'
                                ? 'bg-gray-100 dark:bg-gray-700'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                        title="Biểu đồ đường"
                    >
                        <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4v16" />
                        </svg>
                    </button>
                    <button
                        onClick={() => setChartType('candlestick')}
                        className={`p-1.5 rounded transition-colors ${
                            chartType === 'candlestick'
                                ? 'bg-gray-100 dark:bg-gray-700'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                        title="Biểu đồ nến"
                    >
                        <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                            <rect x="4" y="6" width="3" height="12" rx="0.5" />
                            <rect x="10.5" y="3" width="3" height="18" rx="0.5" />
                            <rect x="17" y="8" width="3" height="8" rx="0.5" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Chart - different based on chart type */}
            <div className="py-1 px-2 border-b border-gray-200 dark:border-gray-700">
                {chartType === 'candlestick' ? (
                    <ComparisonCandlestickChart
                        ticker={activeTicker}
                        timeRange={timeRange}
                        isDarkMode={isDarkMode}
                        chartHeight={chartHeight}
                    />
                ) : (
                    <ComparisonChart
                        allPriceData={priceHistoryCache}
                        tickers={selectedTickersArray}
                        isDarkMode={isDarkMode}
                        timeRange={timeRange}
                        colorMap={colorMap}
                        chartHeight={chartHeight}
                    />
                )}
            </div>

            {/* Stock table - max height with scroll */}
            <div className={`overflow-y-auto ${isMobile ? 'max-h-[180px]' : 'max-h-[240px]'}`}>
                {tickers.map((ticker, idx) => {
                    const tickerType = tickerConfigMap[ticker]?.type || 'stock';
                    const rawData = data[ticker] || data;
                    const tickerData = normalizeStockData(rawData, tickerType);
                    const priceDecimals = tickerType === 'index' ? 2 : 0;
                    return (
                        <StockRow
                            key={ticker}
                            stockData={tickerData}
                            color={STOCK_COLORS[idx % STOCK_COLORS.length]}
                            onClick={(t) => window.open(getTickerUrl(t, tickerType), '_blank')}
                            decimals={priceDecimals}
                            isMobile={isMobile}
                        />
                    );
                })}
            </div>
        </>
    );
}

/**
 * Time range configurations
 */
const TIME_RANGES = [
    { label: '1D', value: '1D', days: 1, interval: '5m' },
    { label: '5D', value: '5D', days: 5, interval: '15m' },
    { label: '1M', value: '1M', days: 30, interval: '1h' },
    { label: '6M', value: '6M', days: 180, interval: '1d' },
    { label: 'YTD', value: 'YTD', days: null, interval: '1d' },
    { label: '1Y', value: '1Y', days: 365, interval: '1d' },
    { label: 'MAX', value: 'MAX', days: 3650, interval: '1d' },
];

/**
 * Format timestamp to full date string (dd/MM/yyyy HH:mm)
 * @param {number} timestamp - Unix timestamp in seconds
 * @param {boolean} includeTime - Whether to include time
 */
function formatDateFull(timestamp, includeTime = false) {
    const date = new Date(timestamp * 1000);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    if (includeTime) {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
    return `${day}/${month}/${year}`;
}

/**
 * Check if two timestamps are on the same day
 */
function isSameDay(ts1, ts2) {
    const d1 = new Date(ts1 * 1000);
    const d2 = new Date(ts2 * 1000);
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
}

/**
 * Single stock price chart with time range selector and chart type toggle
 * @param {object} configParams - Optional params from widget config (interval, from_timestamp, to_timestamp, type)
 * @param chartHeight - Responsive chart height
 */
function SingleStockPriceChart({ ticker, isDarkMode, initialData, configParams = {}, chartHeight = 256 }) {
    const chartRef = useRef(null);
    const [chartType, setChartType] = useState('area'); // 'area' or 'candlestick'
    const [priceData, setPriceData] = useState(initialData || []);
    const [loading, setLoading] = useState(false);
    const [prevClose, setPrevClose] = useState(null);

    // Asset type from config (stock, index, commodity, crypto, economy)
    const assetType = configParams.type || 'stock';

    // Check if custom time range is provided (from_timestamp > 0 means custom range)
    const hasCustomRange = configParams.from_timestamp > 0 && configParams.to_timestamp > 0;

    // Custom range config from params
    const customRangeConfig = useMemo(() => {
        if (!hasCustomRange) return null;
        const from = configParams.from_timestamp;
        const to = configParams.to_timestamp;
        const interval = configParams.interval || '1d';

        // Detect if intraday (same day) - include time in tooltip
        const isIntraday = isSameDay(from, to);
        const tooltip = isIntraday
            ? `${formatDateFull(from, true)} - ${formatDateFull(to, true)}`
            : `${formatDateFull(from)} - ${formatDateFull(to)}`;

        return { from, to, interval, tooltip };
    }, [hasCustomRange, configParams.from_timestamp, configParams.to_timestamp, configParams.interval]);

    // Initial time range - use CUSTOM if custom range provided, otherwise 1Y
    const [timeRange, setTimeRange] = useState(hasCustomRange ? 'CUSTOM' : '1Y');

    // Calculate time range params
    const getTimeParams = (range) => {
        // Handle custom range
        if (range === 'CUSTOM' && customRangeConfig) {
            return {
                from: customRangeConfig.from,
                to: customRangeConfig.to,
                interval: customRangeConfig.interval
            };
        }

        const now = Math.floor(Date.now() / 1000);
        const config = TIME_RANGES.find(r => r.value === range) || TIME_RANGES[5]; // Default 1Y

        let from;
        if (config.value === 'YTD') {
            from = Math.floor(Date.UTC(new Date().getUTCFullYear(), 0, 1) / 1000);
        } else {
            from = now - (config.days * 24 * 60 * 60);
        }

        return { from, to: now, interval: config.interval };
    };

    // Fetch price data when time range changes
    useEffect(() => {
        if (!ticker) return;

        let cancelled = false;

        async function fetchData() {
            setLoading(true);
            try {
                const { from, to, interval } = getTimeParams(timeRange);
                const result = await callWidgetApi('get_historical_prices_range', {
                    tickers: [ticker],
                    interval,
                    from_timestamp: from,
                    to_timestamp: to,
                    type: assetType
                });

                if (!cancelled) {
                    // Backend always returns {ticker: data} format
                    const tickerData = result.data[ticker] || [];
                    setPriceData(tickerData);

                    // Set prev close (first data point's close)
                    if (tickerData.length > 0) {
                        const firstItem = tickerData[0];
                        const close = Array.isArray(firstItem) ? firstItem[4] : firstItem.close;
                        setPrevClose(close);
                    }
                }
            } catch (err) {
                console.warn('Failed to fetch price data:', err);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        fetchData();

        return () => {
            cancelled = true;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticker, timeRange, assetType, customRangeConfig]);

    // Render chart
    useEffect(() => {
        if (loading) return;
        if (!chartRef.current || !priceData || priceData.length === 0) return;

        // Determine color based on price trend
        const firstItem = priceData[0];
        const lastItem = priceData[priceData.length - 1];
        const firstPrice = Array.isArray(firstItem) ? firstItem[4] : firstItem.close;
        const lastPrice = Array.isArray(lastItem) ? lastItem[4] : lastItem.close;
        const isUp = lastPrice >= firstPrice;
        const lineColor = getPriceColor(isUp);

        // Custom range highlight - show when viewing other timeframes
        const showCustomHighlight = customRangeConfig && timeRange !== 'CUSTOM';
        const customFromMs = customRangeConfig ? customRangeConfig.from * 1000 : 0;
        const customToMs = customRangeConfig ? customRangeConfig.to * 1000 : 0;

        // PlotBands and PlotLines for custom range highlight
        const customPlotBands = showCustomHighlight ? [{
            color: isDarkMode ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)',
            from: customFromMs,
            to: customToMs,
            zIndex: 1
        }] : [];

        const customPlotLines = showCustomHighlight ? [
            {
                color: isDarkMode ? 'rgba(59, 130, 246, 0.8)' : 'rgba(59, 130, 246, 0.6)',
                dashStyle: 'Dash',
                width: 2,
                value: customFromMs,
                zIndex: 2,
                label: {
                    text: 'From',
                    align: 'left',
                    rotation: 0,
                    y: 12,
                    style: {
                        color: isDarkMode ? '#93c5fd' : '#3b82f6',
                        fontSize: '10px',
                        fontWeight: '500'
                    }
                }
            },
            {
                color: isDarkMode ? 'rgba(59, 130, 246, 0.8)' : 'rgba(59, 130, 246, 0.6)',
                dashStyle: 'Dash',
                width: 2,
                value: customToMs,
                zIndex: 2,
                label: {
                    text: 'To',
                    align: 'right',
                    rotation: 0,
                    y: 12,
                    style: {
                        color: isDarkMode ? '#93c5fd' : '#3b82f6',
                        fontSize: '10px',
                        fontWeight: '500'
                    }
                }
            }
        ] : [];

        // Determine decimals based on asset type (index uses 2 decimals)
        const priceDecimals = assetType === 'index' ? 2 : 0;

        if (chartType === 'candlestick') {
            // Candlestick chart with volume
            const ohlcData = [];
            const volumeData = [];

            priceData.forEach(item => {
                let ts, open, high, low, close, volume;
                if (Array.isArray(item)) {
                    [ts, open, high, low, close, volume] = item;
                } else {
                    ts = item.timestamp;
                    open = item.open;
                    high = item.high;
                    low = item.low;
                    close = item.close;
                    volume = item.volume || item.totalVolume || 0;
                }
                const timestamp = ts < 1e12 ? ts * 1000 : ts;
                ohlcData.push([timestamp, open, high, low, close]);

                // Volume color based on price change
                const isUpCandle = close >= open;
                volumeData.push({
                    x: timestamp,
                    y: volume || 0,
                    color: getPriceColor(isUpCandle, 0.5)
                });
            });

            // Use shared theme
            const theme = getChartTheme(isDarkMode);
            const textColor = isDarkMode ? 'rgba(229, 229, 229, 0.9)' : 'rgba(51, 51, 51, 0.9)';

            const allPrices = ohlcData.flatMap(([, o, h, l, c]) => [o, h, l, c]).filter(Boolean);
            const dataMin = Math.min(...allPrices);
            const dataMax = Math.max(...allPrices);
            const padding = (dataMax - dataMin) * 0.02;

            Highcharts.chart(chartRef.current, {
                chart: {
                    ...theme.chart,
                    height: chartHeight,
                    zooming: { type: 'x' },
                    panning: { enabled: true, type: 'x' },
                    panKey: 'shift',
                },
                title: { text: null },
                credits: theme.credits,
                exporting: { enabled: false },
                legend: { enabled: false },
                xAxis: {
                    ...theme.xAxis,
                    type: 'datetime',
                    ordinal: true,
                    plotBands: customPlotBands,
                    plotLines: customPlotLines,
                    events: {
                        afterSetExtremes: function(e) {
                            const chart = this.chart;
                            const visible = ohlcData.filter(([t]) => t >= e.min && t <= e.max);
                            if (!visible.length) return;
                            const prices = visible.flatMap(([, o, h, l, c]) => [o, h, l, c]).filter(Boolean);
                            const vMin = Math.min(...prices);
                            const vMax = Math.max(...prices);
                            const pad = (vMax - vMin) * 0.02;
                            chart.yAxis[0].setExtremes(vMin - pad, vMax + pad, true, false);
                        }
                    }
                },
                yAxis: [{
                    // Price axis
                    ...theme.yAxis,
                    height: '75%',
                    min: dataMin - padding,
                    max: dataMax + padding,
                    labels: {
                        ...theme.yAxis.labels,
                        align: 'right',
                        x: -5,
                        formatter: function() { return formatNumber(this.value, { decimals: priceDecimals }); }
                    },
                    opposite: false
                }, {
                    // Volume axis
                    title: { text: null },
                    top: '78%',
                    height: '22%',
                    offset: 0,
                    gridLineWidth: 0,
                    labels: { enabled: false }
                }],
                tooltip: {
                    ...theme.tooltip,
                    shared: true,
                    formatter: function() {
                        const points = this.points;
                        let html = `<div style="font-size:11px;margin-bottom:5px;color:${textColor}">${Highcharts.dateFormat('%d/%m/%Y', this.x)}</div>`;
                        points.forEach(point => {
                            if (point.series.type === 'candlestick') {
                                html += `<div style="padding:2px 0;color:${textColor}"><b>O:</b> ${formatNumber(point.point.open, { decimals: priceDecimals })} <b>H:</b> ${formatNumber(point.point.high, { decimals: priceDecimals })} <b>L:</b> ${formatNumber(point.point.low, { decimals: priceDecimals })} <b>C:</b> ${formatNumber(point.point.close, { decimals: priceDecimals })}</div>`;
                            } else if (point.series.type === 'column') {
                                html += `<div style="padding:2px 0;color:${textColor}"><b>KL:</b> ${formatNumber(point.y, { compact: true })}</div>`;
                            }
                        });
                        return html;
                    }
                },
                plotOptions: theme.plotOptions,
                series: [{
                    type: 'candlestick',
                    name: ticker,
                    data: ohlcData,
                    yAxis: 0
                }, {
                    type: 'column',
                    name: 'Volume',
                    data: volumeData,
                    yAxis: 1
                }]
            });
        } else {
            // Area chart with gradient fill
            const chartData = priceData.map(item => {
                const [timestamp, , , , close] = Array.isArray(item) ? item : [item.timestamp, 0, 0, 0, item.close];
                return [timestamp < 1e12 ? timestamp * 1000 : timestamp, close];
            });

            // Use shared theme
            const theme = getChartTheme(isDarkMode);

            Highcharts.chart(chartRef.current, {
                chart: {
                    ...theme.chart,
                    type: 'area',
                    height: chartHeight,
                },
                title: { text: null },
                credits: theme.credits,
                exporting: { enabled: false },
                legend: { enabled: false },
                xAxis: {
                    ...theme.xAxis,
                    type: 'datetime',
                    plotBands: customPlotBands,
                    plotLines: customPlotLines,
                },
                yAxis: {
                    ...theme.yAxis,
                    labels: {
                        ...theme.yAxis.labels,
                        align: 'right',
                        x: -5,
                        formatter: function() { return formatNumber(this.value, { decimals: 0 }); }
                    },
                    opposite: false,
                    plotLines: prevClose ? [{
                        value: prevClose,
                        color: isDarkMode ? '#6b7280' : '#9ca3af',
                        dashStyle: 'Dash',
                        width: 1,
                        label: {
                            text: `Prev: ${formatNumber(prevClose, { decimals: 0 })}`,
                            align: 'right',
                            style: { color: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: '10px' }
                        }
                    }] : []
                },
                tooltip: {
                    ...theme.tooltip,
                    xDateFormat: '%d/%m/%Y %H:%M',
                    pointFormat: '<b>{point.y:,.0f}</b> VNĐ'
                },
                plotOptions: {
                    area: {
                        fillColor: {
                            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                            stops: [
                                [0, getPriceColor(isUp, 0.3)],
                                [1, getPriceColor(isUp, 0.02)]
                            ]
                        },
                        lineColor: lineColor,
                        lineWidth: 2,
                        marker: { enabled: false },
                        states: {
                            hover: { lineWidth: 2 }
                        },
                        threshold: null
                    }
                },
                series: [{
                    data: chartData,
                    name: ticker
                }]
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priceData, ticker, isDarkMode, chartType, prevClose, customRangeConfig, chartHeight, loading]);

    return (
        <div className="border-b border-gray-100 dark:border-gray-700">
            {/* Time range selector + Chart type toggle */}
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                {/* Time ranges - horizontal scroll */}
                <div className="flex-1 overflow-x-auto scrollbar-hide">
                    <div className="flex items-center gap-0.5 min-w-max">
                        {TIME_RANGES.map(({ label, value }) => (
                            <button
                                key={value}
                                onClick={() => setTimeRange(value)}
                                className={`px-2 py-1 text-[11px] font-medium rounded transition-colors whitespace-nowrap ${
                                    timeRange === value
                                        ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200'
                                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                        {/* Custom range button - only show if custom range was provided */}
                        {customRangeConfig && (
                            <button
                                onClick={() => setTimeRange('CUSTOM')}
                                title={customRangeConfig.tooltip}
                                className={`px-2 py-1 text-[11px] font-medium rounded transition-colors whitespace-nowrap ${
                                    timeRange === 'CUSTOM'
                                        ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200'
                                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                            >
                                Custom
                            </button>
                        )}
                    </div>
                </div>

                {/* Chart type toggle */}
                <div className="flex-shrink-0 flex items-center gap-0.5 border border-gray-200 dark:border-gray-600 rounded p-0.5">
                    <button
                        onClick={() => setChartType('area')}
                        className={`p-1 rounded transition-colors ${
                            chartType === 'area'
                                ? 'bg-gray-100 dark:bg-gray-700'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                        title="Area Chart"
                    >
                        <svg className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4v16" />
                        </svg>
                    </button>
                    <button
                        onClick={() => setChartType('candlestick')}
                        className={`p-1 rounded transition-colors ${
                            chartType === 'candlestick'
                                ? 'bg-gray-100 dark:bg-gray-700'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                        title="Candlestick Chart"
                    >
                        <svg className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                            <rect x="4" y="6" width="3" height="12" rx="0.5" />
                            <rect x="10.5" y="3" width="3" height="18" rx="0.5" />
                            <rect x="17" y="8" width="3" height="8" rx="0.5" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Chart */}
            <div className="px-2 py-1">
                <div ref={chartRef} className="w-full" style={{ height: `${chartHeight}px` }} />
            </div>
        </div>
    );
}

/**
 * Single stock card content (used for both single and tabbed view)
 * @param chartHeight - Responsive chart height
 */
function StockCardContent({ stockData, priceHistory, loadingHistory, isDarkMode, configParams = {}, chartHeight = 256, isMobile = false }) {
    if (!stockData) {
        return (
            <div className="p-4">
                <p className="text-gray-500">No stock data available</p>
            </div>
        );
    }

    const {
        ticker,
        nameVi,
        nameEn,
        priceClose,
        netChange,
        pctChange,
        priceOpen,
        priceHigh,
        priceLow,
        totalVolume,
        marketCap,
        peRatio,
        pbRatio,
        epsRatio,
        roe,
        stockExchange,
        imageUrl
    } = stockData;

    const displayName = nameVi || nameEn || ticker;

    // Determine decimals based on asset type (index uses 2 decimals)
    const assetType = configParams.type || 'stock';
    const priceDecimals = assetType === 'index' ? 2 : 0;

    return (
        <>
            {/* Header */}
            <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-3">
                    {/* Logo */}
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt={ticker}
                            className="w-10 h-10 rounded-full object-contain bg-gray-100"
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                    ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                            {ticker?.charAt(0) || '?'}
                        </div>
                    )}

                    {/* Name and Exchange */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="font-bold text-lg text-gray-900 dark:text-white">
                                {ticker}
                            </span>
                            {stockExchange && (
                                <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-300">
                                    {stockExchange}
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                            {displayName}
                        </p>
                    </div>

                    {/* Price */}
                    <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                            {formatNumber(priceClose, { decimals: priceDecimals })}
                        </div>
                        <div className="text-sm">
                            <PriceChange value={netChange} percent={pctChange} decimals={priceDecimals} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Price Chart with time range selector */}
            <SingleStockPriceChart
                ticker={ticker}
                isDarkMode={isDarkMode}
                initialData={priceHistory}
                configParams={configParams}
                chartHeight={chartHeight}
            />

            {/* Key Metrics - Responsive layout */}
            <div className="px-3 py-3 border-b border-gray-100 dark:border-gray-700">
                {isMobile ? (
                    /* Mobile: compact grid with label on top */
                    <div className="grid grid-cols-3 gap-x-3 gap-y-3">
                        <div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">Đóng cửa</div>
                            <div className="text-xs font-medium text-gray-900 dark:text-white">
                                {formatNumber(priceClose - netChange, { decimals: priceDecimals })}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">Vốn hóa</div>
                            <div className="text-xs font-medium text-gray-900 dark:text-white">
                                {marketCap ? formatNumber(marketCap, { compact: true }) : '-'}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">P/E</div>
                            <div className="text-xs font-medium text-gray-900 dark:text-white">
                                {peRatio ? peRatio.toFixed(2) : '-'}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">Mở cửa</div>
                            <div className="text-xs font-medium text-gray-900 dark:text-white">
                                {formatNumber(priceOpen, { decimals: priceDecimals })}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">Khối lượng</div>
                            <div className="text-xs font-medium text-gray-900 dark:text-white">
                                {formatNumber(totalVolume, { compact: true })}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">EPS</div>
                            <div className="text-xs font-medium text-gray-900 dark:text-white">
                                {epsRatio ? formatNumber(epsRatio, { decimals: 0 }) : '-'}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">Biên độ</div>
                            <div className="text-xs font-medium text-gray-900 dark:text-white">
                                {formatNumber(priceLow, { decimals: priceDecimals })}-{formatNumber(priceHigh, { decimals: priceDecimals })}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">P/B</div>
                            <div className="text-xs font-medium text-gray-900 dark:text-white">
                                {pbRatio ? pbRatio.toFixed(2) : '-'}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">ROE</div>
                            <div className="text-xs font-medium text-gray-900 dark:text-white">
                                {roe ? `${roe.toFixed(1)}%` : '-'}
                            </div>
                        </div>
                    </div>
                ) : (
                    /* Desktop: 3-column layout with dividers */
                    <div className="flex">
                        {/* Column 1 */}
                        <div className="flex-1 border-r border-gray-200 dark:border-gray-700 pr-6 space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500 dark:text-gray-400">Đóng cửa</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {formatNumber(priceClose - netChange, { decimals: priceDecimals })}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500 dark:text-gray-400">Mở cửa</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {formatNumber(priceOpen, { decimals: priceDecimals })}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500 dark:text-gray-400">Biên độ</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {formatNumber(priceLow, { decimals: priceDecimals })}-{formatNumber(priceHigh, { decimals: priceDecimals })}
                                </span>
                            </div>
                        </div>
                        {/* Column 2 */}
                        <div className="flex-1 border-r border-gray-200 dark:border-gray-700 px-6 space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500 dark:text-gray-400">Vốn hóa</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {marketCap ? formatNumber(marketCap, { compact: true }) : '-'}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500 dark:text-gray-400">Khối lượng</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {formatNumber(totalVolume, { compact: true })}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500 dark:text-gray-400">P/B</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {pbRatio ? pbRatio.toFixed(2) : '-'}
                                </span>
                            </div>
                        </div>
                        {/* Column 3 */}
                        <div className="flex-1 pl-6 space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500 dark:text-gray-400">P/E</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {peRatio ? peRatio.toFixed(2) : '-'}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500 dark:text-gray-400">EPS</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {epsRatio ? formatNumber(epsRatio, { decimals: 0 }) : '-'}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500 dark:text-gray-400">ROE</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {roe ? `${roe.toFixed(1)}%` : '-'}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer - Analysis Button */}
            <div className="px-4 py-3 flex justify-center">
                <a
                    href={getTickerUrl(ticker, configParams.type || 'stock', true)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors"
                >
                    {isMobile
                        ? (configParams.type === 'stock' || !configParams.type ? 'Xem phân tích 360' : 'Xem biểu đồ')
                        : (configParams.type === 'stock' || !configParams.type ? 'Xem phân tích 360 trên Simplize' : 'Xem biểu đồ trên Simplize')
                    }
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </a>
            </div>
        </>
    );
}

/**
 * Main StockInfoCard component
 */
export default function StockInfoCard({ data, config }) {
    const [priceHistoryCache, setPriceHistoryCache] = useState({});
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [activeTab, setActiveTab] = useState(null);
    const [comparisonTimeRange, setComparisonTimeRange] = useState('1Y');

    const handleComparisonTimeRangeChange = (range) => {
        if (range === comparisonTimeRange) return;
        setComparisonTimeRange(range);
        setPriceHistoryCache({});
    };

    // Use reusable hook for responsive container width detection
    const { containerRef, containerWidth, isMobile, isCompact } = useContainerWidth(500);

    // Calculate responsive chart height
    const chartHeight = useMemo(() => {
        if (isCompact) {
            // Very small: 60% of width, min 150, max 200
            return Math.max(150, Math.min(200, Math.round(containerWidth * 0.55)));
        }
        if (isMobile) {
            // Mobile: 55% of width, min 180, max 240
            return Math.max(180, Math.min(240, Math.round(containerWidth * 0.55)));
        }
        // Desktop: fixed 256px
        return 256;
    }, [containerWidth, isMobile, isCompact]);

    // Detect dark mode
    useEffect(() => {
        const checkDarkMode = () => {
            setIsDarkMode(document.documentElement.classList.contains('dark'));
        };
        checkDarkMode();

        const observer = new MutationObserver(checkDarkMode);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

        return () => observer.disconnect();
    }, []);

    // Parse params array and build ticker-to-config mapping
    const { tickers, tickerConfigMap } = useMemo(() => {
        const allTickers = [];
        const configMap = {}; // ticker -> { type, interval, from_timestamp, to_timestamp }

        // Handle params as array
        const paramsArray = Array.isArray(config?.params) ? config.params : (config?.params ? [config.params] : []);

        for (const paramGroup of paramsArray) {
            const groupTickers = paramGroup.tickers || [];
            const groupConfig = {
                type: paramGroup.type || 'stock',
                interval: paramGroup.interval || '1d',
                from_timestamp: paramGroup.from_timestamp ?? -1,
                to_timestamp: paramGroup.to_timestamp ?? 0
            };

            for (const ticker of groupTickers) {
                if (!allTickers.includes(ticker)) {
                    allTickers.push(ticker);
                    configMap[ticker] = groupConfig;
                }
            }
        }

        // Fallback: extract from data if no params
        if (allTickers.length === 0) {
            if (data?.ticker) {
                allTickers.push(data.ticker);
                configMap[data.ticker] = { type: 'stock', interval: '1d', from_timestamp: -1, to_timestamp: 0 };
            } else if (data && typeof data === 'object' && !Array.isArray(data)) {
                const keys = Object.keys(data).filter(k => !k.startsWith('_'));
                for (const k of keys) {
                    if (data[k]?.ticker) {
                        allTickers.push(k);
                        configMap[k] = { type: 'stock', interval: '1d', from_timestamp: -1, to_timestamp: 0 };
                    }
                }
            }
        }

        return { tickers: allTickers, tickerConfigMap: configMap };
    }, [config, data]);

    // Get config params for active ticker
    const activeTickerConfig = useMemo(() => {
        return tickerConfigMap[activeTab] || { type: 'stock', interval: '1d', from_timestamp: -1, to_timestamp: 0 };
    }, [tickerConfigMap, activeTab]);

    // Set initial active tab
    useEffect(() => {
        if (tickers.length > 0 && !activeTab) {
            setActiveTab(tickers[0]);
        }
    }, [tickers, activeTab]);

    // Get stock data for active ticker (normalized based on asset type)
    const activeStockData = useMemo(() => {
        if (!data || !activeTab) return null;

        let rawData;

        // If data is keyed by ticker
        if (data[activeTab]) {
            rawData = data[activeTab];
        }
        // If data is single object with ticker
        else if (data.ticker === activeTab) {
            rawData = data;
        }
        // Fallback
        else {
            rawData = data;
        }

        // Normalize based on asset type
        const assetType = tickerConfigMap[activeTab]?.type || 'stock';
        return normalizeStockData(rawData, assetType);
    }, [data, activeTab, tickerConfigMap]);

    // Fetch 1-year price history for active ticker
    useEffect(() => {
        if (!activeTab) return;

        // Check cache
        if (priceHistoryCache[activeTab]) return;

        let cancelled = false;

        async function fetchHistory() {
            setLoadingHistory(true);
            try {
                const now = Math.floor(Date.now() / 1000);
                const from = now - (365 * 24 * 60 * 60); // 1 year
                const assetType = tickerConfigMap[activeTab]?.type || 'stock';

                const result = await callWidgetApi('get_historical_prices_range', {
                    tickers: [activeTab],
                    interval: '1d',
                    from_timestamp: from,
                    to_timestamp: now,
                    type: assetType
                });

                if (!cancelled) {
                    // Backend always returns {ticker: data} format
                    setPriceHistoryCache(prev => ({
                        ...prev,
                        [activeTab]: result.data[activeTab] || []
                    }));
                }
            } catch (err) {
                console.warn('Failed to fetch price history:', err);
            } finally {
                if (!cancelled) {
                    setLoadingHistory(false);
                }
            }
        }

        fetchHistory();

        return () => {
            cancelled = true;
        };
    }, [activeTab, priceHistoryCache, tickerConfigMap]);

    // Use comparison view for multiple tickers
    const useComparisonView = tickers.length > 1;

    // Fetch history for ALL tickers when in comparison mode
    useEffect(() => {
        if (!useComparisonView) return;

        const rangeConfig = CANDLESTICK_TIME_RANGES[comparisonTimeRange] || CANDLESTICK_TIME_RANGES['1Y'];

        async function fetchAllHistory() {
            const tickersToFetch = tickers.filter(t => !priceHistoryCache[t]);
            if (tickersToFetch.length === 0) return;

            setLoadingHistory(true);
            try {
                const now = Math.floor(Date.now() / 1000);
                const from = rangeConfig.days
                    ? now - rangeConfig.days * 24 * 60 * 60
                    : Math.floor(Date.UTC(new Date().getUTCFullYear(), 0, 1) / 1000);

                await Promise.all(tickersToFetch.map(async (ticker) => {
                    try {
                        const assetType = tickerConfigMap[ticker]?.type || 'stock';
                        const result = await callWidgetApi('get_historical_prices_range', {
                            tickers: [ticker],
                            interval: rangeConfig.interval,
                            from_timestamp: from,
                            to_timestamp: now,
                            type: assetType
                        });
                        setPriceHistoryCache(prev => ({
                            ...prev,
                            [ticker]: result.data[ticker] || []
                        }));
                    } catch (err) {
                        console.warn(`Failed to fetch history for ${ticker}:`, err);
                    }
                }));
            } finally {
                setLoadingHistory(false);
            }
        }

        fetchAllHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [useComparisonView, tickers, tickerConfigMap, comparisonTimeRange]);

    if (!data) {
        return (
            <div className="border border-gray-100 dark:border-gray-800 p-4 rounded-xl" style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
                <p className="text-gray-500">No stock data available</p>
            </div>
        );
    }

    return (
        <div ref={containerRef} className="border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden" style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
            {useComparisonView ? (
                /* Comparison view for multiple tickers */
                <StockComparisonView
                    data={data}
                    tickers={tickers}
                    priceHistoryCache={priceHistoryCache}
                    loadingHistory={loadingHistory && Object.keys(priceHistoryCache).length < tickers.length}
                    isDarkMode={isDarkMode}
                    tickerConfigMap={tickerConfigMap}
                    chartHeight={chartHeight}
                    isMobile={isMobile}
                    timeRange={comparisonTimeRange}
                    onTimeRangeChange={handleComparisonTimeRangeChange}
                />
            ) : (
                /* Single stock view */
                <StockCardContent
                    stockData={activeStockData}
                    priceHistory={priceHistoryCache[activeTab]}
                    loadingHistory={loadingHistory && !priceHistoryCache[activeTab]}
                    isDarkMode={isDarkMode}
                    configParams={activeTickerConfig}
                    chartHeight={chartHeight}
                    isMobile={isMobile}
                />
            )}
        </div>
    );
}
